import React from 'react';
import { PageType } from '../App';
import UseCaseMMC from '../components/UseCaseMMC';
import HowItWorksSection from '../components/HowItWorksSection';

interface FounderPageProps {
  setCurrentPage: (page: PageType) => void;
}

const FounderPage: React.FC<FounderPageProps> = ({ setCurrentPage }) => {
  return (
    <>
      <section className="uc-hero">
        <div className="uc-hero-inner">
          <span className="uc-hero-label">For Founders &amp; Entrepreneurs</span>
          <h1>Know before<br/><em>you commit.</em></h1>
          <p>You're about to stake runway, time, and credibility on one market decision. We simulate exactly what happens when your company enters — revenue, burn, break-even — so you go in knowing, not hoping.</p>
          <a href="#" onClick={(e) => { e.preventDefault(); setCurrentPage('home'); }} className="uc-hero-cta">Start Your Free Simulation</a>
          <div style={{display:'flex',alignItems:'center',justifyContent:'center',marginTop:'20px'}}>
            <div style={{display:'inline-flex',alignItems:'center',gap:'10px',padding:'12px 16px',background:'rgba(255,255,255,0.05)',borderRadius:'10px',border:'1px solid rgba(255,255,255,0.1)',maxWidth:'400px'}}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#7c9a6e" strokeWidth="2" strokeLinecap="round" style={{flexShrink:0}}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              <p style={{fontSize:'12px',color:'rgba(255,255,255,0.45)',lineHeight:'1.5',margin:0}}><strong style={{color:'rgba(255,255,255,0.7)',fontWeight:600}}>Your data stays private.</strong> Strictly confidential. Never shared without your permission.</p>
            </div>
          </div>
        </div>
      </section>

      <div className="uc-section">
        <div className="uc-label"><span>What to Get</span></div>
        <h2 className="uc-title">The numbers that tell you<br/><em>if it's worth the bet.</em></h2>
        <p className="uc-sub">Every week in the wrong market burns runway you can't recover. Here's what we give you before you commit.</p>
        <div className="uc-features">
          <div className="uc-feature">
            <div className="uc-feature-icon"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></div>
            <h3>A clear go/no-go verdict</h3>
            <p>Not a report full of maybes. A direct verdict on whether your target market is worth entering — based on your business model, your team size, and your budget. Something you can actually make a decision on.</p>
          </div>
          <div className="uc-feature">
            <div className="uc-feature-icon"><svg viewBox="0 0 24 24"><path d="M18 6C16.6 4.7 14.9 4 13 4C8.6 4 5 7.6 5 12s3.6 8 8 8c1.9 0 3.6-.7 5-1.9"/><line x1="3" y1="10" x2="15" y2="10"/><line x1="3" y1="14" x2="15" y2="14"/></svg></div>
            <h3>Month-by-month cashflow</h3>
            <p>See exactly when your burn peaks, when the first revenue lands, and when you reach break-even — across three scenarios. You'll know if your runway is enough before you've signed a single contract.</p>
          </div>
          <div className="uc-feature">
            <div className="uc-feature-icon"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg></div>
            <h3>The true cost of entering</h3>
            <p>Local salaries, employer taxes, entity setup, and customer acquisition costs sourced from companies already operating in your target market. Not estimates. What you will actually spend to get up and running.</p>
          </div>
          <div className="uc-feature">
            <div className="uc-feature-icon"><svg viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg></div>
            <h3>Data your investors will trust</h3>
            <p>A 12-month model with multi-market comparison gives you something most founders don't walk into fundraising with — a well-researched, independently built financial case for why this market, why now.</p>
          </div>
        </div>
      </div>

      <UseCaseMMC />
      <HowItWorksSection type="founder" />
    </>
  );
};

export default FounderPage;
